import { Col, Form, InputNumber, Row, Select } from "antd";
import { useEffect, useState } from "react";
import InModal from "../../controls/InModal";
import { modalTitle } from "../../controls/InModal.default";
import ModalFooter from "../../controls/ModalFooter";
import ModalDeleteConfirmation from "../../controls/ModalDeleteConfirmation";
import ModalErrorAlert from "../../controls/ModalErrorAlert";
import { getProblemMessage } from "../../utils/http";
import {
  getCompanies,
  getCompanyDepartments,
  getCompanyFiscalPeriods,
  addOrganizationalMetric,
  updateOrganizationalMetric,
  deleteOrganizationalMetric,
} from "../../repository/data.repository";
import {
  MAX_PAGE_SIZE,
  MetricType,
  OrganizationalMetricEntry,
} from "../../repository/data.default";

const METRIC_TYPES: MetricType[] = [
  "Absenteeism",
  "Turnover",
  "Grievance",
  "Accident",
];

interface Option {
  readonly label: string;
  readonly value: string;
}

export interface OrganizationalMetricFormDialogProps {
  readonly open?: boolean;
  readonly mode: "add" | "update" | "delete";
  readonly initialValues?: OrganizationalMetricEntry;
  readonly onFinish?: () => void;
  readonly onCancel?: () => void;
}

function OrganizationalMetricFormDialog(
  props: OrganizationalMetricFormDialogProps,
) {
  const [form] = Form.useForm();
  const companyUid = Form.useWatch("companyUid", form);
  const metricType: MetricType | undefined = Form.useWatch("metricType", form);

  const [companies, setCompanies] = useState<Option[]>([]);
  const [departments, setDepartments] = useState<Option[]>([]);
  const [periods, setPeriods] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  useEffect(() => {
    if (!props.open) {
      form.resetFields();
      setErrorMessage(undefined);
      return;
    }
    getCompanies({ searchQuery: { conditions: [] }, pageNumber: 0, pageSize: 200 })
      .then(({ data }) =>
        setCompanies(data.map((c) => ({ label: c.name, value: c.uid }))),
      )
      .catch(() => setCompanies([]));
    if (props.initialValues) {
      form.setFieldsValue(props.initialValues);
    }
  }, [props.open]);

  useEffect(() => {
    setDepartments([]);
    setPeriods([]);
    if (!companyUid) return;
    const scoped = {
      searchQuery: {
        conditions: [
          { field: "companyUid", operation: 0 as any, values: [companyUid] },
        ],
      },
      pageNumber: 0,
      pageSize: MAX_PAGE_SIZE,
    };
    getCompanyDepartments(scoped)
      .then(({ data }) =>
        setDepartments(data.map((d) => ({ label: d.name, value: d.uid }))),
      )
      .catch(() => setDepartments([]));
    getCompanyFiscalPeriods(scoped)
      .then(({ data }) =>
        setPeriods(
          data
            .filter((p) => p.periodType === ("Quarter" as any) || true)
            .map((p) => ({ label: p.name, value: p.uid })),
        ),
      )
      .catch(() => setPeriods([]));
  }, [companyUid]);

  function onSubmit(): void {
    setLoading(true);
    setErrorMessage(undefined);
    if (props.mode === "delete") {
      deleteOrganizationalMetric(props.initialValues?.uid!)
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch((e) => {
          setLoading(false);
          setErrorMessage(getProblemMessage(e));
        });
      return;
    }

    form
      .validateFields()
      .then((v) => {
        const payload = {
          companyUid: v.companyUid,
          departmentUid: v.departmentUid,
          companyFiscalPeriodUid: v.companyFiscalPeriodUid,
          metricType: v.metricType as MetricType,
          headcount: v.headcount ?? 0,
          scheduledWorkdays: v.scheduledWorkdays ?? null,
          eventCount: v.eventCount ?? 0,
          voluntaryDepartures: v.voluntaryDepartures ?? null,
          involuntaryDepartures: v.involuntaryDepartures ?? null,
          retirements: v.retirements ?? null,
        };
        const promise =
          props.mode === "add"
            ? addOrganizationalMetric(payload)
            : updateOrganizationalMetric({
                uid: props.initialValues?.uid,
                rowVersion: props.initialValues?.rowVersion,
                ...payload,
              });
        return promise.then(() => {
          setLoading(false);
          props.onFinish?.();
        });
      })
      .catch((e) => {
        setLoading(false);
        if (e?.errorFields) return; // form validation errors are shown inline
        setErrorMessage(getProblemMessage(e));
      });
  }

  const isAbsenteeism = metricType === "Absenteeism";
  const isTurnover = metricType === "Turnover";

  return (
    <InModal
      open={props.open}
      size={props.mode === "delete" ? "small" : "medium"}
      title={modalTitle(props.mode, "Metric Entry", undefined)}
      onCancel={props.onCancel}
      actions={
        <ModalFooter
          mode={props.mode}
          loading={loading}
          onCancel={props.onCancel}
          onSubmit={onSubmit}
        />
      }
    >
      {props.mode === "delete" ? (
        <ModalDeleteConfirmation
          entityLabel={`${props.initialValues?.metricType} entry for ${props.initialValues?.departmentName}`}
        />
      ) : (
        <Form form={form} layout="vertical">
          <Form.Item
            name="companyUid"
            label="Company"
            rules={[{ required: true, message: "Company is required" }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Company"
              options={companies}
              onChange={() => {
                form.setFieldsValue({
                  departmentUid: undefined,
                  companyFiscalPeriodUid: undefined,
                });
              }}
            />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name="departmentUid"
                label="Department"
                rules={[{ required: true, message: "Department is required" }]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder="Department"
                  disabled={!companyUid}
                  options={departments}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="companyFiscalPeriodUid"
                label="Fiscal period (quarter)"
                rules={[{ required: true, message: "Fiscal period is required" }]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder="Period"
                  disabled={!companyUid}
                  options={periods}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="metricType"
            label="Metric"
            rules={[{ required: true, message: "Metric is required" }]}
          >
            <Select
              placeholder="Metric"
              options={METRIC_TYPES.map((t) => ({ label: t, value: t }))}
            />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name="headcount"
                label="Headcount (period start)"
                rules={[{ required: true, message: "Headcount is required" }]}
              >
                <InputNumber className="w-full" min={0} />
              </Form.Item>
            </Col>
            {isAbsenteeism && (
              <Col span={12}>
                <Form.Item name="scheduledWorkdays" label="Scheduled workdays">
                  <InputNumber className="w-full" min={0} />
                </Form.Item>
              </Col>
            )}
          </Row>
          {isTurnover ? (
            <Row gutter={12}>
              <Col span={8}>
                <Form.Item name="voluntaryDepartures" label="Voluntary">
                  <InputNumber className="w-full" min={0} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="involuntaryDepartures" label="Involuntary">
                  <InputNumber className="w-full" min={0} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="retirements" label="Retirements">
                  <InputNumber className="w-full" min={0} />
                </Form.Item>
              </Col>
            </Row>
          ) : (
            <Form.Item
              name="eventCount"
              label={
                isAbsenteeism
                  ? "Absent days"
                  : metricType === "Grievance"
                    ? "Grievances filed"
                    : metricType === "Accident"
                      ? "Accidents"
                      : "Event count"
              }
            >
              <InputNumber className="w-full" min={0} />
            </Form.Item>
          )}
          <ModalErrorAlert message={errorMessage} />
        </Form>
      )}
    </InModal>
  );
}

export default OrganizationalMetricFormDialog;
